import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["scroll", "tableCol"]
  static values = { subdomain: String }

  connect() {
    const subdomain = this.subdomainValue
    console.log("[WaitlistDisplay] Connecting with subdomain:", subdomain)

    if (!subdomain) {
      console.error("[WaitlistDisplay] No subdomain value found — cannot subscribe")
      this.startPolling() // fallback
      return
    }

    this.subscription = consumer.subscriptions.create(
      { channel: "WaitlistDisplayChannel", subdomain: subdomain },
      {
        connected: () => {
          console.log("[WaitlistDisplay] ✅ ActionCable connected")
          this.cableConnected = true
          this.stopPolling() // cable works, no need for polling
        },
        disconnected: () => {
          console.warn("[WaitlistDisplay] ⚠️ ActionCable disconnected — starting polling fallback")
          this.startPolling()
        },
        rejected: () => {
          console.error("[WaitlistDisplay] ❌ ActionCable subscription rejected (club not found for subdomain?)")
          this.startPolling()
        },
        received: (data) => {
          console.log("[WaitlistDisplay] Received update:", data.type, "table_id:", data.table_id)
          this.handleUpdate(data)
        }
      }
    )

    // Start polling as a safety net after 5s — if cable connects it will stop
    this.pollingFallbackTimer = setTimeout(() => {
      if (!this.cableConnected) {
        console.warn("[WaitlistDisplay] Cable not confirmed after 5s — enabling polling fallback")
        this.startPolling()
      }
    }, 5000)
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.stopPolling()
    clearTimeout(this.pollingFallbackTimer)
  }

  // --- ActionCable handler ---

  handleUpdate(data) {
    switch (data.type) {
      case "full_reload":
        window.location.reload()
        return

      case "background_update": {
        const bgEl = document.getElementById('wl-bg')
        if (bgEl) bgEl.style.backgroundImage = data.url ? `url('${data.url}')` : ''
        return
      }

      case "size_update": {
        const root = document.documentElement
        if (data.logo_size) root.style.setProperty('--wl-logo-size', data.logo_size + 'px')
        if (data.title_size) root.style.setProperty('--wl-title-size', data.title_size + 'px')
        if (data.logo_left !== undefined) root.style.setProperty('--wl-logo-left', data.logo_left + 'px')
        return
      }

      case "title_update":
        const titleEl = document.getElementById('wl-title')
        if (titleEl && data.title !== undefined) titleEl.textContent = data.title
        return

      case "logo_update":
        const logoEl = document.getElementById('wl-logo')
        if (data.url) {
          if (logoEl) {
            logoEl.src = data.url
          } else {
            // Logo didn't exist — reload to render the img tag properly
            window.location.reload()
          }
        } else if (logoEl) {
          logoEl.remove() // logo was deleted
        }
        return

      case "table_update":
        if (!data.table_id) return
        const colEl = document.getElementById(`wl-table-col-${data.table_id}`)
        if (!colEl) {
          // New table we don't have a column for yet — reload
          window.location.reload()
          return
        }
        colEl.innerHTML = this.buildColumnHTML(data)
        return
    }
  }

  // --- Polling fallback ---

  startPolling() {
    if (this.pollInterval) return // already polling
    console.log("[WaitlistDisplay] Starting polling every 10s")
    this.poll()
    this.pollInterval = setInterval(() => this.poll(), 10000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
      console.log("[WaitlistDisplay] Polling stopped")
    }
  }

  async poll() {
    try {
      const url = window.location.origin + '/waitinglist/data'
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()

      // Update background if changed
      if (data.active_background_url && data.active_background_url !== this.currentBgUrl) {
        this.currentBgUrl = data.active_background_url
        const bgEl = document.getElementById('wl-bg')
        if (bgEl) bgEl.style.backgroundImage = `url('${data.active_background_url}')`
      }

      // Detect table structure changes (new or removed tables) → reload
      const tables = data.tables || []
      const currentIds = tables.map(t => t.id)
      const domIds = [...document.querySelectorAll('[id^="wl-table-col-"]')]
        .map(el => parseInt(el.id.replace('wl-table-col-', '')))

      const added = currentIds.some(id => !domIds.includes(id))
      const removed = domIds.some(id => !currentIds.includes(id))
      if (added || removed) { window.location.reload(); return }

      // Update existing table columns
      tables.forEach(t => {
        const col = document.getElementById(`wl-table-col-${t.id}`)
        if (col) col.innerHTML = this.buildColumnHTML(t)
      })
    } catch (e) {
      console.warn("[WaitlistDisplay] Poll error:", e)
    }
  }

  // --- DOM builder ---

  buildColumnHTML(d) {
    const available = d.available_seats || 0
    const gameInfo = [d.game_type, d.stakes].filter(Boolean).join(" • ")

    const seatsBadge = available > 0
      ? `<span class="wl-seats-badge available">
           <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24">
             <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z"/>
           </svg>
           ${available} ${available === 1 ? 'seat' : 'seats'} available
         </span>`
      : `<span class="wl-seats-badge full">
           <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
             <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
           </svg>
           Full
         </span>`

    const entries = (d.waitlist || []).map((entry, idx) => `
      <div class="wl-entry" id="wl-entry-${entry.id}">
        <div class="wl-entry-pos">${idx + 1}</div>
        <div class="wl-entry-name">${this.esc(entry.name)}</div>
        ${entry.card_number ? `<div class="wl-entry-card">#${this.esc(String(entry.card_number))}</div>` : ''}
      </div>
    `).join('')

    const emptyState = `
      <div class="wl-empty">
        <svg class="wl-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
          <line x1="8" y1="18" x2="21" y2="18"/>
          <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/>
          <line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
        <span class="wl-empty-text">No players waiting</span>
      </div>`

    return `
      <div class="wl-col-header">
        <div class="wl-col-table-name">${this.esc(d.table_name || '')}</div>
        ${gameInfo ? `<div class="wl-col-table-info">${this.esc(gameInfo)}</div>` : ''}
        ${seatsBadge}
      </div>
      <div class="wl-col-entries">
        ${entries.length > 0 ? entries : emptyState}
      </div>
    `
  }

  esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}
